/* Isolated from video-media: the original section remains the comparison baseline. */
(() => {
  if (customElements.get('optimized-column-video')) return;
  class OptimizedColumnVideo extends HTMLElement {
    connectedCallback() {
      // Wait until parser-created children exist. Cloned cards retain the inert template.
      this.setupFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.visible = false;
        this.setAttribute('aria-busy', this.hasAttribute('loaded') ? 'false' : 'true');
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            this.visible = entry.isIntersecting;
            if (this.visible) {
              this.abortRetryAvailable = true;
              this.activate();
            }
            else this.video?.pause();
          }
        });
        this.observer.observe(this);
        this.visibilityHandler = () => {
          if (document.hidden) this.video?.pause();
          else if (this.visible) this.activate();
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
      });
    }

    activate() {
      if (document.hidden) return;
      if (!this.video) {
        const template = this.querySelector('template');
        const source = template?.content.querySelector('video');
        if (!source) return;
        const video = source.cloneNode(true);
        this.video = video;
        if (this.hasAttribute('autoplay')) {
          video.muted = true;
          video.defaultMuted = true;
        }
        video.playsInline = true;
        video.autoplay = false;
        video.preload = 'auto';
        // Keep the template in place, so carousel clones never depend on source timing.
        video.addEventListener('loadeddata', () => {
          if ((!video.requestVideoFrameCallback || !this.hasAttribute('autoplay')) && video.readyState >= 2) {
            requestAnimationFrame(() => {
              if (this.isConnected && this.video === video) this.revealFrame();
            });
          }
        });
        video.addEventListener('canplay', () => {
          if (this.visible && this.hasAttribute('autoplay')) this.startPlayback();
        });
        video.addEventListener('error', () => {
          this.removeAttribute('loaded');
          this.setAttribute('aria-busy', 'false');
          this.setAttribute('media-error', String(video.error?.code || 'unknown'));
          console.error('Optimized multicolumn video failed', video.currentSrc, video.error);
        });
        this.appendChild(video);
        // Exactly once, after insertion and playback properties have been set.
        video.load();
      }
      if (this.hasAttribute('autoplay')) this.startPlayback();
    }

    startPlayback() {
      const video = this.video;
      if (!video || !this.visible || document.hidden || this.playPending || !video.paused) return;
      this.playPending = true;
      let interrupted = false;
      video.play().then(() => {
        if (!this.isConnected || !this.visible || document.hidden) {
          video.pause();
          return;
        }
        this.removeAttribute('suspended');
        if (video.requestVideoFrameCallback && this.frameCallback == null) {
          this.frameCallback = video.requestVideoFrameCallback(() => {
            this.frameCallback = null;
            if (this.isConnected && this.video === video) this.revealFrame();
          });
        }
      }).catch(error => {
        // A visibility-triggered pause can interrupt a pending play. Re-entry retries.
        interrupted = error.name === 'AbortError';
        if (!interrupted) {
          this.setAttribute('suspended', '');
          this.setAttribute('aria-busy', 'false');
          console.warn('Optimized multicolumn playback rejected', video.currentSrc, error);
        }
      }).finally(() => {
        this.playPending = false;
        if (interrupted && this.abortRetryAvailable && this.visible && !document.hidden) {
          this.abortRetryAvailable = false;
          // Only retry a visibility race, never an error or an autoplay rejection.
          if (this.isConnected && this.video === video) this.startPlayback();
        }
      });
    }

    revealFrame() {
      this.setAttribute('loaded', '');
      this.setAttribute('aria-busy', 'false');
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.frameCallback != null) this.video?.cancelVideoFrameCallback(this.frameCallback);
      this.frameCallback = null;
      this.video?.pause();
    }
  }
  customElements.define('optimized-column-video', OptimizedColumnVideo);
})();
